// Measuring an experiment: the same slice of trades (e.g. "trades right after a loss")
// before the experiment started and while it ran, over the same number of trading days.
import { netPnl, tradingDayKey, currentTradingDay } from './pnl';
import { rootSymbol } from './tools/contracts';
import { HOLD_BUCKETS, RESET_TEXT, holdMinutes } from './insights';

// Fewer trades than this during the experiment, and the result is labeled early.
export const SOLID_SAMPLE = 10;

function isWeekday(key) {
  const day = new Date(`${key}T12:00:00Z`).getUTCDay();
  return day >= 1 && day <= 5;
}

function addDays(key, n) {
  const d = new Date(`${key}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Trading days (Monday to Friday) from `from` to `to`, inclusive.
export function tradingDaysBetween(from, to) {
  let n = 0;
  for (let k = from; k <= to; k = addDays(k, 1)) if (isWeekday(k)) n++;
  return n;
}

// The trading day `n` trading days after `from` (n = 1 is `from` itself, if a weekday).
function nthTradingDay(from, n) {
  let k = from;
  let count = isWeekday(k) ? 1 : 0;
  while (count < n) {
    k = addDays(k, 1);
    if (isWeekday(k)) count++;
  }
  return k;
}

// The last `n` trading days before `key`: [first, last].
function daysBefore(key, n) {
  let k = addDays(key, -1);
  let count = 0;
  let first = k;
  while (count < n) {
    if (isWeekday(k)) {
      count++;
      first = k;
    }
    k = addDays(k, -1);
  }
  return [first, addDays(key, -1)];
}

// Which day of the experiment it is, and whether it's finished.
export function experimentProgress(exp, today = currentTradingDay()) {
  const last = exp.endedOn || today;
  const day = Math.max(0, Math.min(exp.lengthDays, tradingDaysBetween(exp.startedOn, last)));
  return { day, length: exp.lengthDays, finished: exp.status !== 'active' || day >= exp.lengthDays, lastDay: nthTradingDay(exp.startedOn, exp.lengthDays) };
}

// Trades from every account that belong to the slice the experiment is about.
function slice(metric, trades, notes) {
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  switch (metric.kind) {
    case 'time': {
      const key = Number(metric.key);
      return sorted.filter((t) => {
        const d = new Date(t.date);
        return Math.floor((d.getHours() * 60 + d.getMinutes()) / 30) * 30 === key;
      });
    }
    case 'weekday':
      return sorted.filter((t) => String(new Date(`${tradingDayKey(t.date)}T12:00:00`).getDay()) === String(metric.key));
    case 'afterLoss':
    case 'afterWin':
      return sorted.filter((t, i) => {
        const prev = sorted[i - 1];
        if (!prev || tradingDayKey(prev.date) !== tradingDayKey(t.date)) return false;
        return metric.kind === 'afterLoss' ? netPnl(prev) < 0 : netPnl(prev) > 0;
      });
    case 'lateTrades': {
      const seen = {};
      return sorted.filter((t) => {
        const k = tradingDayKey(t.date);
        seen[k] = (seen[k] || 0) + 1;
        return seen[k] >= 3;
      });
    }
    case 'side':
      return sorted.filter((t) => (t.side || 'Long') === metric.key);
    case 'size':
      return sorted.filter((t) => rootSymbol(t.symbol) === metric.key && (t.size || 1) > 1);
    case 'hold':
      return sorted.filter((t) => holdMinutes(t) !== null && HOLD_BUCKETS.findIndex((b) => holdMinutes(t) < b.max) === Number(metric.key));
    case 'tag':
      return sorted.filter((t) => (t.tags || []).includes(metric.key));
    case 'reset': {
      const taps = [];
      for (const n of Object.values(notes || {})) {
        for (const e of (n && n.sessionLog) || []) {
          if (e.feeling || (e.type === 'feeling' && RESET_TEXT.test(e.text || ''))) taps.push(new Date(e.time).getTime());
        }
      }
      return sorted.filter((t) => {
        const at = new Date(t.date).getTime();
        return taps.some((tap) => at >= tap && at - tap <= 15 * 60000);
      });
    }
    default:
      return sorted;
  }
}

function summarize(list, days) {
  const nets = list.map(netPnl);
  const net = nets.reduce((a, b) => a + b, 0);
  return {
    count: list.length,
    net,
    winRate: list.length ? (nets.filter((n) => n > 0).length / list.length) * 100 : null,
    days,
    perDay: days ? net / days : 0,
    tradesPerDay: days ? list.length / days : 0,
  };
}

// Before vs during for one experiment. "Before" covers as many trading days as the
// experiment has run so far (at least 5), ending the day before it started.
export function experimentResults(exp, trades, notes, today = currentTradingDay()) {
  const progress = experimentProgress(exp, today);
  const duringEnd = exp.endedOn && exp.endedOn < progress.lastDay ? exp.endedOn : progress.lastDay;
  const span = Math.max(5, progress.day);
  const [beforeStart, beforeEnd] = daysBefore(exp.startedOn, span);
  const inRange = (from, to) => trades.filter((t) => {
    const k = tradingDayKey(t.date);
    return k >= from && k <= to;
  });
  const before = summarize(slice(exp.metric, inRange(beforeStart, beforeEnd), notes), span);
  const during = summarize(slice(exp.metric, inRange(exp.startedOn, duringEnd), notes), Math.max(1, progress.day));
  // Days the trader said they stuck to it, from the evening reflection.
  const checked = Object.entries(notes || {}).filter(([k, n]) => k >= exp.startedOn && k <= duringEnd && n && n.experimentFollowed);
  const kept = checked.filter(([, n]) => n.experimentFollowed === 'yes').length;
  return { progress, before, during, early: during.count < SOLID_SAMPLE, adherence: { checked: checked.length, kept } };
}
